/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * A guide pick's Owners block in the community row style (contract #217, 2026-10-18; plan
 * docs/best-list-owners-community-row-plan.md) — ONE component for every surface a pick has: the
 * old style's block under a roster row (`GuidePick`), the hidden crawler copy inside every
 * section strip (`GuidePick`, `crawl`; on best lists, review posts and plain posts alike), the
 * desktop side panel (`PickFocusBody`) and the phone sheet (`PickSheet`). (2026-10-18 →
 * 2026-10-21 also the block at the end of a new-style review, `GuideOwners`.)
 *
 *  - The summary line: "From owners", then "N owner reviews · M of N would buy again · <photo /
 *    hours facts>" from the pick's aggregate, and "All N →" at its right — the product's
 *    community page, the By product group head's link (`productHubHref`).
 *  - Then the pick's newest reviews (up to three, newest first — DealHunt's `owners.items`, each
 *    EXACTLY a `/feed` item) as the community's own condensed feed rows (`FeedRow`, as the By
 *    product minis): author, "Owner · 1–2 years", buy-again, the sentence, the ✓ / ✕ reads, the
 *    vote line with "Ask <handle>". No second renderer: a change to the row is a change to the
 *    feed's. The vote line shows only where DealHunt sent `vote_counts` (Constraint 420b85cb —
 *    `FeedRow` decides). No pager, no "as expected" chips, no "Reply →", no "Read all".
 *  - Without `items` (a DealHunt before #217's republish) the summary line alone; without an
 *    owner (count 0) nothing — the callers say "None yet".
 *
 * Note text once per SERVER HTML (Constraint dcbf4ee5): ONE strip per pick renders this block
 * `hidden` for crawlers (`crawl`, 2026-10-21 — `strip-rules.ts` says which; the panel is what
 * readers see); the panel and the sheet mount on a click, client-only. Where rows show, the
 * block mounts ONE beacon over them (each `FeedRow` carries `data-note-id`), as `ProductIndex`
 * does over its minis — except the `crawl` copy, which is never seen: no beacon, so no view is
 * counted and no `owner_views_sent` fires from it. (The `compact` summary-line variant of the
 * end-of-review block went with that block, 2026-10-21.)
 *
 * "All N →" is the product's community page — on a review post, the product page's owners
 * anchor instead (`ownersDeepLink`): the card is the review's one link to its product page
 * (Constraint a4ba5f91), and the strip inherited that from the owner card it replaced.
 *
 * The reader's own votes are one batched read per page (`GuideOwnersProvider`), with one memory
 * of answers for every copy of a review on the page, and the rows' ages are printed against the
 * page's build time, so the ISR HTML and the hydrated page agree.
 */
import { useRef, useState } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import styles from './PickOwners.module.css'
import { useGuideOwners } from './GuideOwnersProvider'
import FeedRow from 'components/community/FeedRow'
import { useHelpfulMine } from 'components/community/useHelpfulMine'
import feed from 'components/community/Feed.module.css'
import { useOwnerTrack, useOwnerViewBeacon } from 'lib/owner-notes/analytics'
import { ownersDeepLink } from 'lib/owner-notes/band-copy'
import { ownersHeadline, ownersSubline, type CardOwnersView } from 'lib/owner-notes/card-owners'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { productHubHref } from 'lib/community/model'
import type { CommunityHelpfulAnswer, FeedControlAnalytics } from 'lib/community/types'
import { productSlugFromHref } from 'lib/community/writer-handoff'
import { publicConfig } from 'config'

/** Where the block is — the rows' vote / Ask `location` on `community_page_owner_*` events:
 *  the desktop side panel, the phone sheet, or a strip's hidden crawler copy (`card`). */
export type PickOwnersSurface = 'panel' | 'sheet' | 'card'

const NO_IDS: string[] = []

interface PickOwnersProps {
  owners: CardOwnersView | null
  /** The pick's battery spec — the "vs … h claimed" half of the hours line. */
  claimedHours: number | null
  pdpHref: string | null
  nodeId: string
  productName: string
  surface: PickOwnersSurface
  /** The crawlers' copy inside a `hidden` wrapper: the same block, no view beacon (never seen). */
  crawl?: boolean
}

const PickOwners: React.VFC<PickOwnersProps> = ({
  owners,
  claimedHours,
  pdpHref,
  nodeId,
  productName,
  surface,
  crawl = false,
}) => {
  const trackOwner = useOwnerTrack({ nodeId, name: productName })
  const pageType = useCatalogEvent().scope?.page_type
  const trailingSlash = publicConfig('useTrailingSlash')
  const rootRef = useRef<HTMLElement>(null)
  const items = owners ? owners.items : []
  const ids = items.map(item => item.note.id)
  // The page's shared votes and build time (`GuideOwnersProvider`). Outside it — a page built
  // before `guideOwners`, a spec — the block reads its own ≤3 votes, keeps its own memory, and
  // prints ages against its first render.
  const shared = useGuideOwners()
  const ownMine = useHelpfulMine(shared ? NO_IDS : ids)
  const [ownMemory] = useState(() => new Map<string, CommunityHelpfulAnswer>())
  const [ownNow] = useState(() => Date.now())
  // One beacon over the rows; a block without rows shows no note and registers no `views_sent`
  // listener (the flush tells the FIRST listener — it must be a block that showed one).
  useOwnerViewBeacon(rootRef, {
    enabled: ids.length > 0 && !crawl,
    deps: [ids.join(',')],
    report: ids.length > 0 && !crawl,
  })

  if (!owners) {
    return null
  }
  const count = `${owners.count} owner review${owners.count === 1 ? '' : 's'}`
  // `ownersSubline` leads with the owner count when the headline is the buy-again line; the
  // summary here already opens with the count, so that part is dropped, the rest kept.
  const extras = ownersSubline({ ...owners, buyAgain: null }, claimedHours)
  const verdict = owners.buyAgain && owners.buyAgain.total > 0 ? ownersHeadline(owners) : null
  const summary = [count, verdict, extras].filter(Boolean).join(' · ')
  // The product's community page: the rows' own product slug, else the PDP's (the same slug —
  // what follows `/product/`), so "All N →" is there before DealHunt sends `items` too.
  const slug = items[0]?.product.slug || productSlugFromHref(pdpHref)
  let all = slug ? productHubHref(slug, trailingSlash) : null
  if (pageType === 'review_page' && pdpHref) {
    all = ownersDeepLink(pdpHref)
  }
  // The rows' vote / Ask events name the page they are on; a guide when no page says.
  const analytics: FeedControlAnalytics = { page_type: pageType ?? 'guide_page', location: surface }

  return (
    <section className={styles.root} ref={rootRef} aria-label={`Owners of the ${productName}`}>
      <header className={styles.head}>
        <p className={styles.heading}>
          <span className={styles.eyebrow}>{'From owners'}</span>
          <span className={styles.summary}>{summary}</span>
        </p>
        {all && (
          <Linked
            href={all}
            className={styles.all}
            onClick={() =>
              trackOwner('notes_click', { location: 'pick', item_count: owners.count })
            }
          >
            {`All ${owners.count.toLocaleString('en-US')} →`}
          </Linked>
        )}
      </header>
      {items.length > 0 && (
        <div className={clsx(styles.rows, feed.indexMinis)}>
          {items.map(item => (
            <FeedRow
              key={item.note.id}
              item={item}
              now={shared?.now ?? ownNow}
              condensed
              showProduct={false}
              helpfulMemory={shared?.helpfulMemory ?? ownMemory}
              helpfulMine={shared?.helpfulMine ?? ownMine}
              analytics={analytics}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default PickOwners
